import {
  FiSave,
  FiEdit2,
  FiTrash2,
  FiX,
  FiImage,
  FiLink,
} from "react-icons/fi";
import utils from "../../helpers/utils";
import { useEffect, useState, FormEvent, ChangeEvent } from "react";
import { LinkButton } from "../../types";

interface LinkButtonItemProps {
  data: LinkButton;
  onClick?: (data: LinkButton) => void;
  onSubmit: (data: LinkButton) => void;
  onRemove: (id: string) => void;
}

function LinkButtonItem({
  data,
  onClick,
  onSubmit,
  onRemove,
}: LinkButtonItemProps) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Partial<LinkButton>>(data);
  const [imagePreview, setImagePreview] = useState<string>("");

  useEffect(() => {
    if (open) {
      setForm(data);
      if (data?.image) setImagePreview(utils.getUriImage(data?.image));
    }
  }, [open, data]);

  useEffect(() => {
    if (data.open) {
      setOpen(true);
    }
  }, [data]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (form) {
      onSubmit({ ...data, ...form, open: undefined } as LinkButton);
      setOpen(false);
      setForm({});
    }
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setForm({ ...form, image: base64String });
        setImagePreview(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const isSchedule = !!form.is_schedule;

  return (
    <div className="w-full rounded-lg border border-stroke bg-white shadow-sm dark:border-strokedark dark:bg-boxdark">
      {/* Header do Card */}
      <div className="flex items-center justify-between p-4">
        <div
          className="flex cursor-pointer items-center gap-4"
          onClick={() => (onClick ? onClick(data) : setOpen(!open))}
        >
          {data?.image && (
            <div className="h-12 w-12 overflow-hidden rounded-md bg-gray-100 dark:bg-gray-800">
              <img
                src={utils.getUriImage(data?.image)}
                alt={data.label || "Link button"}
                className="h-full w-full object-contain"
              />
            </div>
          )}
          <span className="font-semibold text-black dark:text-white">
            {data.label}
          </span>
        </div>

        <div className="flex items-center gap-3">
          {!open ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(!open);
              }}
              className="rounded-md p-2 text-gray-600 transition hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-meta-4"
            >
              <FiEdit2 size={18} />
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (
                    window.confirm("Tem certeza que deseja remover este botão?")
                  ) {
                    onRemove(data.id);
                  }
                }}
                className="rounded-md p-2 text-red-600 transition hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
              >
                <FiTrash2 size={18} />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(!open);
                }}
                className="rounded-md p-2 text-gray-600 transition hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-meta-4"
              >
                <FiX size={18} />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Formulário de Edição */}
      {open && (
        <form
          onSubmit={submit}
          className="border-t border-stroke p-6 dark:border-strokedark"
        >
          <div className="flex flex-col gap-6 lg:flex-row">
            {/* Coluna da Imagem */}
            <div className="w-full lg:w-1/3">
              <label className="mb-3 block text-sm font-medium text-black dark:text-white">
                Imagem <span className="text-red-500">*</span>
              </label>

              {/* Preview */}
              <div className="mb-4 flex h-40 w-full items-center justify-center overflow-hidden rounded-lg border-2 border-dashed border-stroke bg-gray-50 dark:border-strokedark dark:bg-gray-800">
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <FiImage size={40} className="text-gray-400" />
                )}
              </div>

              {/* Upload Button */}
              <label className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-stroke bg-white px-4 py-2.5 text-sm font-medium text-black transition hover:bg-gray-50 dark:border-strokedark dark:bg-boxdark dark:text-white dark:hover:bg-meta-4">
                <FiImage size={18} />
                Selecionar Imagem
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
            </div>

            {/* Coluna dos Inputs */}
            <div className="flex w-full flex-col gap-5 lg:w-2/3">
              {/* Título */}
              <div className="w-full">
                <label className="mb-3 block text-sm font-medium text-black dark:text-white">
                  Título <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={form.label || ""}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setForm({ ...form, label: e.target.value })
                  }
                  placeholder="Ex: Instagram"
                  className="w-full rounded border border-stroke bg-transparent px-5 py-3 outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                />
              </div>

              {/* URL */}
              <div className="w-full">
                <label className="mb-3 block text-sm font-medium text-black dark:text-white">
                  URL de Redirecionamento{" "}
                  {!isSchedule && <span className="text-red-500">*</span>}
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2">
                    <FiLink className="text-gray-500" size={20} />
                  </span>
                  <input
                    type="url"
                    required={!isSchedule}
                    disabled={isSchedule}
                    readOnly={isSchedule}
                    value={form.url || ""}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setForm({ ...form, url: e.target.value })
                    }
                    placeholder="https://exemplo.com"
                    className="w-full rounded border border-stroke bg-transparent py-3 pl-12 pr-4 outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary text-black dark:text-white disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                  />
                </div>
              </div>

              {/* Opções de Agendamento */}
              <div className="w-full space-y-3">
                <div className="flex items-center gap-3">
                  <input
                    id={`is_schedule_${data.id}`}
                    type="checkbox"
                    checked={!!form.is_schedule}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setForm({
                        ...form,
                        is_schedule: e.target.checked,
                      })
                    }
                    className="h-4 w-4 rounded border-stroke text-primary focus:ring-primary"
                  />
                  <label
                    htmlFor={`is_schedule_${data.id}`}
                    className="text-sm font-medium text-black dark:text-white"
                  >
                    Usar este botão como botão de agendamento
                  </label>
                </div>

                {isSchedule && (
                  <div className="flex items-center gap-3">
                    <input
                      id={`enabled_${data.id}`}
                      type="checkbox"
                      checked={form.enabled !== false}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setForm({
                          ...form,
                          enabled: e.target.checked,
                        })
                      }
                      className="h-4 w-4 rounded border-stroke text-primary focus:ring-primary"
                    />
                    <label
                      htmlFor={`enabled_${data.id}`}
                      className="text-sm text-black dark:text-white"
                    >
                      Botão de agendar ativo
                    </label>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Botão Salvar */}
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded border border-stroke px-5 py-2.5 font-medium text-black transition hover:bg-gray-50 dark:border-strokedark dark:text-white dark:hover:bg-meta-4"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="inline-flex items-center gap-2 rounded bg-primary px-5 py-2.5 font-medium text-white transition hover:bg-opacity-90"
            >
              <FiSave size={18} />
              Salvar Alterações
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default LinkButtonItem;
